import {motion, usePresence} from 'framer-motion';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {forwardRef, ReactNode, Ref, useEffect} from 'react';

import {always} from 'ramda';

import {DialogSize} from '../types/DialogSize';
import {dialogTransitions} from '../utils/dialogTransitions';

interface StyledDialogSectionProps {
  scrollBehavior?: 'inside' | 'outside';
  isEnlarged?: boolean;
  size?: DialogSize;
  disableMaxHeight?: boolean;
}

const StyledDialogSection = styled(motion.section)<StyledDialogSectionProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  outline: 0;
  width: 100%;
  height: ${(props) =>
    props.size === 'xLarge' ? `calc(100% - ${props.theme.getSize(8)})` : 'auto'};
  background-color: ${(props) => props.theme.colors.palettes.white[10][100]};
  box-shadow: ${(props) => props.theme.shadows.elevation_4};
  border-width: 1px;
  border-radius: ${(props) => props.theme.radii.medium};
  border-color: ${(props) => props.theme.colors.palettes.neutral[40][100]};
  margin: ${(props) =>
    match<DialogSize | undefined>(props.size)
      .with('xLarge', always(props.theme.getSize(4)))
      .otherwise(always(`0 ${props.theme.getSize(15)}`))};
  max-height: ${(props) =>
    props.scrollBehavior === 'outside' || props.size === 'xLarge' || props.disableMaxHeight
      ? 'none'
      : `calc(100% - ${props.theme.getSize(props.isEnlarged ? 8 : 30)})`};
  max-width: ${(props) =>
    match<DialogSize | undefined>(props.size)
      .with('small', always('400px'))
      .with('large', always('1200px'))
      .with('xLarge', always('2000px'))
      .otherwise(always('800px'))};
  ${(props) =>
    props.scrollBehavior === 'outside' ? 'margin-top: auto; margin-bottom: auto;' : ''};
`;

export interface DialogTransitionProps {
  size?: DialogSize;
  preset?: 'slideInBottom' | 'slideInRight' | 'scale' | 'none';
  children: ReactNode;
  isEnlarged?: boolean;
  scrollBehavior?: 'inside' | 'outside';
  disableMaxHeight?: boolean;
}

// eslint-disable-next-line typescript/no-explicit-any -- Pre-existing any
export const DialogTransition = forwardRef((props: DialogTransitionProps, ref: Ref<any>) => {
  const motionProps = dialogTransitions[props.preset ?? 'none'];

  const [isPresent, safeToRemove] = usePresence();

  useEffect(
    function scheduleRemovalOnExit() {
      if (!isPresent && safeToRemove) {
        setTimeout(safeToRemove);
      }
    },
    [isPresent, safeToRemove]
  );

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  return (
    <StyledDialogSection
      ref={ref}
      onClick={handleClick}
      {...motionProps}
      scrollBehavior={props.scrollBehavior}
      isEnlarged={props.isEnlarged}
      size={props.size}
      disableMaxHeight={props.disableMaxHeight}
    >
      {props.children}
    </StyledDialogSection>
  );
});
